import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  User,
  Award,
  Clock,
  Heart,
  CheckCircle,
  Mail,
  Phone,
  MapPin,
  Globe,
} from "lucide-react";

export default function UeberMich() {
  const { t } = useTranslation();

  const qualities = [
    {
      title: t('aboutMe.qualities.experience.title'),
      description: t('aboutMe.qualities.experience.description'),
      icon: Clock,
    },
    {
      title: t('aboutMe.qualities.italian.title'),
      description: t('aboutMe.qualities.italian.description'),
      icon: Award,
    },
    {
      title: t('aboutMe.qualities.quality.title'),
      description: t('aboutMe.qualities.quality.description'),
      icon: CheckCircle,
    },
    {
      title: t('aboutMe.qualities.reliability.title'),
      description: t('aboutMe.qualities.reliability.description'),
      icon: Heart,
    },
  ];

  const languages = [
    { name: "Deutsch" },
    { name: "Italiano" },
    { name: "English" },
    { name: "Español" },
  ];

  return (
    <div data-testid="ueber-mich-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-white mb-6">
              {t('aboutMe.hero.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('aboutMe.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Profile Section */}
      <section className="py-16 md:py-24 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Image */}
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_mt-innenausbau/artifacts/9j329ezy_WhatsApp%20Image%202026-01-30%20at%2013.32.49.jpeg"
                  alt={t('aboutMe.profile.name')}
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-orange-500 rounded-xl -z-10" />
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-500">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold font-['Manrope'] text-[#2D3748]">
                    {t('aboutMe.profile.name')}
                  </h2>
                  <p className="text-gray-600">{t('aboutMe.profile.role')}</p>
                </div>
              </div>

              <div className="prose prose-gray max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {t('aboutMe.profile.bio1')}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  {t('aboutMe.profile.bio2')}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t('aboutMe.profile.bio3')}
                </p>
              </div>

              {/* Languages */}
              <div className="mt-8 p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="h-5 w-5 text-orange-500" />
                  <span className="font-medium text-[#2D3748]">{t('aboutMe.profile.multilingual')}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {languages.map((lang, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700"
                    >
                      {lang.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Qualities Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="qualities-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#2D3748] mb-4">
              {t('aboutMe.qualities.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('aboutMe.qualities.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {qualities.map((quality, index) => (
              <Card
                key={index}
                className="border bg-gray-50 hover:shadow-lg transition-all duration-300"
                data-testid={`quality-card-${index}`}
              >
                <CardContent className="p-6 flex gap-4">
                  <div className="p-3 rounded-xl bg-orange-100 text-orange-500 h-fit">
                    <quality.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-2">
                      {quality.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{quality.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 md:py-24 bg-[#2D3748]" data-testid="ueber-mich-contact">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-6">
                {t('aboutMe.contact.title')}
              </h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                {t('aboutMe.contact.description')}
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-gray-300">
                  <MapPin className="h-5 w-5 text-orange-400" />
                  <span>Uelzen und Umgebung</span>
                </div>
                <a
                  href="tel:+4915207684235"
                  className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors"
                >
                  <Phone className="h-5 w-5 text-orange-400" />
                  <span>+49 152 07684235</span>
                </a>
                <a
                  href="mailto:info@mtrenovierung.de"
                  className="flex items-center gap-3 text-gray-300 hover:text-orange-400 transition-colors"
                >
                  <Mail className="h-5 w-5 text-orange-400" />
                  <span>info@mtrenovierung.de</span>
                </a>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-lg h-14 px-8"
                data-testid="ueber-mich-email-button"
              >
                <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20für%20Renovierungsarbeiten">
                  <Mail className="mr-2 h-5 w-5" />
                  {t('common.sendEmail')}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#2D3748] text-lg h-14 px-8 bg-transparent"
                data-testid="ueber-mich-phone-button"
              >
                <a href="tel:+4915207684235">
                  <Phone className="mr-2 h-5 w-5" />
                  {t('common.callNow')}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
